import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, HardDrive, Link2, Search, FolderOpen, CheckCircle2, Trash2, FileText, RefreshCw, Unplug } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${Math.round(n / 1024)} KB` : `${n || 0} B`);

/** Standard-document library (uploads + Google Drive). Pass onPick to use it as a picker. */
export function AttachmentLibraryDialog({ open, onOpenChange, onPick, title }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [tab, setTab] = useState('library');
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [loading, setLoading] = useState(false);
  const [upForm, setUpForm] = useState({ name: '', category: 'Other', description: '', file: null });
  const [uploading, setUploading] = useState(false);
  const [drive, setDrive] = useState(null);
  const [driveFiles, setDriveFiles] = useState(null);
  const [folderInput, setFolderInput] = useState('');
  const [importing, setImporting] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/rfp/attachments`, { withCredentials: true });
      setItems(r.data.items); setCats(r.data.categories);
    } catch { toast.error('Could not load the attachment library'); }
    finally { setLoading(false); }
  };
  const loadDrive = async () => {
    try { const r = await axios.get(`${API_URL}/api/rfp/drive/status`, { withCredentials: true }); setDrive(r.data); return r.data; }
    catch { setDrive({ configured: false }); return null; }
  };
  useEffect(() => { if (open) { load(); loadDrive(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async () => {
    if (!upForm.file) { toast.error('Choose a file first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', upForm.file); fd.append('name', upForm.name); fd.append('category', upForm.category); fd.append('description', upForm.description);
      await axios.post(`${API_URL}/api/rfp/attachments`, fd, { withCredentials: true });
      toast.success('Added to the library');
      setUpForm({ name: '', category: 'Other', description: '', file: null });
      setTab('library'); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.name}" from the library?`)) return;
    try { await axios.delete(`${API_URL}/api/rfp/attachments/${it.id}`, { withCredentials: true }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not remove'); }
  };
  const connect = async () => {
    try { const r = await axios.get(`${API_URL}/api/rfp/drive/connect`, { withCredentials: true }); window.location.href = r.data.authorization_url; }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not start Google sign-in'); }
  };
  const disconnect = async () => {
    try { await axios.delete(`${API_URL}/api/rfp/drive/disconnect`, { withCredentials: true }); setDriveFiles(null); loadDrive(); }
    catch { toast.error('Could not disconnect'); }
  };
  const listDrive = async (folder) => {
    setDriveFiles('loading');
    try { const r = await axios.get(`${API_URL}/api/rfp/drive/files`, { params: folder ? { folder } : {}, withCredentials: true }); setDriveFiles(r.data.files); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not list the folder'); setDriveFiles(null); }
  };
  const saveFolder = async () => {
    try {
      const r = await axios.put(`${API_URL}/api/rfp/drive/folder`, { folder: folderInput }, { withCredentials: true });
      toast.success(`Folder "${r.data.name}" nominated for the team`); setFolderInput(''); await loadDrive(); listDrive(r.data.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not open that folder'); }
  };
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  useEffect(() => {
    if (tab !== 'drive') return;
    axios.get(`${API_URL}/api/rfp/drive/sync`, { withCredentials: true }).then(r => setLastSync(r.data.last)).catch(() => {});
  }, [tab]);
  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API_URL}/api/rfp/drive/sync`, {}, { withCredentials: true });
      setLastSync(r.data);
      toast.success(`Drive sync: ${r.data.checked} checked, ${r.data.updated} updated${r.data.errors.length ? `, ${r.data.errors.length} error(s)` : ''}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };
  const importFile = async (f) => {
    setImporting(m => ({ ...m, [f.id]: true }));
    try {
      await axios.post(`${API_URL}/api/rfp/drive/import`, { file_id: f.id, category: 'Other' }, { withCredentials: true });
      toast.success(`"${f.name}" imported into the library`); load(); listDrive(drive?.folder?.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setImporting(m => ({ ...m, [f.id]: false })); }
  };

  const shown = items.filter(it => (cat === 'all' || it.category === cat) && (!q || `${it.name} ${it.description} ${it.tags.join(' ')} ${it.filename}`.toLowerCase().includes(q.toLowerCase())));
  const TabBtn = ({ id, icon: Icon, label }) => (
    <button type="button" onClick={() => setTab(id)} className={`h-7 px-2.5 text-[11px] rounded-md inline-flex items-center gap-1 ${tab === id ? 'bg-[#0F2040] text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid={`rfp-lib-tab-${id}`}>
      <Icon className="w-3 h-3" />{label}
    </button>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rfp-attachment-library">
        <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><FolderOpen className="w-4 h-4 text-[#4ABFCF]" />{title || 'Attachment library'}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1.5 flex-wrap">
          <TabBtn id="library" icon={FileText} label={`Library (${items.length})`} />
          <TabBtn id="upload" icon={Upload} label="Upload" />
          <TabBtn id="drive" icon={HardDrive} label="Google Drive" />
        </div>
        {tab === 'library' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1"><Search className="w-3 h-3 absolute left-2 top-2.5 text-slate-400" /><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, description, tags…" className="h-8 text-xs pl-7" data-testid="rfp-lib-search" /></div>
              <select value={cat} onChange={e => setCat(e.target.value)} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="rfp-lib-cat">
                <option value="all">All categories</option>{cats.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {loading ? <p className="text-xs text-slate-400 py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</p>
              : shown.length === 0 ? <p className="text-xs text-slate-400 py-6 text-center" data-testid="rfp-lib-empty">No documents yet — upload standard documents (certificates, company profile, insurance, policies) or import them from Google Drive.</p>
                : (
                  <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                    {shown.map(it => (
                      <div key={it.id} className="p-2 flex items-center gap-2 text-xs" data-testid={`rfp-lib-item-${it.id}`}>
                        <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-[#0F2040] truncate flex items-center gap-1">{it.name} <Badge variant="outline" className="text-[9px]">{it.category}</Badge>{it.source === 'drive' && <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">Drive</Badge>}</div>
                          <p className="text-[10px] text-slate-400 truncate">{it.filename} · {fmtSize(it.size)}{it.description ? ` · ${it.description}` : ''}</p>
                        </div>
                        <a href={`${API_URL}/api/rfp/files/${it.path}`} target="_blank" rel="noreferrer" className="text-[10px] text-[#4ABFCF] hover:underline" data-testid={`rfp-lib-open-${it.id}`}>open</a>
                        {onPick && <Button size="sm" className="h-6 text-[10px] px-2 bg-[#0F2040] hover:bg-[#1A3055] text-white" onClick={() => onPick(it)} data-testid={`rfp-lib-pick-${it.id}`}><CheckCircle2 className="w-3 h-3 mr-1" />Use</Button>}
                        {isAdmin && <button type="button" onClick={() => remove(it)} className="text-slate-300 hover:text-rose-500" title="Remove" data-testid={`rfp-lib-del-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    ))}
                  </div>
                )}
          </div>
        )}
        {tab === 'upload' && (
          <div className="space-y-2" data-testid="rfp-lib-upload-form">
            <input type="file" onChange={e => setUpForm(f => ({ ...f, file: e.target.files?.[0] || null, name: f.name || (e.target.files?.[0]?.name || '').replace(/\.[^.]+$/, '') }))} className="text-xs" data-testid="rfp-lib-upload-file" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={upForm.name} onChange={e => setUpForm(f => ({ ...f, name: e.target.value }))} placeholder="Document name" className="h-8 text-xs" data-testid="rfp-lib-upload-name" />
              <select value={upForm.category} onChange={e => setUpForm(f => ({ ...f, category: e.target.value }))} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="rfp-lib-upload-cat">
                {(cats.length ? cats : ['Other']).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Input value={upForm.description} onChange={e => setUpForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description (what it evidences, validity…)" className="h-8 text-xs" data-testid="rfp-lib-upload-desc" />
            <Button size="sm" onClick={upload} disabled={uploading} className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-lib-upload-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}Add to library
            </Button>
          </div>
        )}
        {tab === 'drive' && (
          <div className="space-y-2" data-testid="rfp-lib-drive">
            {!drive ? <p className="text-xs text-slate-400">Checking…</p>
              : !drive.configured ? <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded p-2" data-testid="rfp-drive-unconfigured">Google Drive isn't configured on this server yet — an admin needs to add the Google OAuth credentials.</p>
                : !drive.connected ? (
                  <div className="text-xs text-slate-600 space-y-2">
                    <p>Connect your Google account (read-only) to browse the team's RFP attachments folder and import documents into the library.</p>
                    <Button size="sm" onClick={connect} className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-drive-connect-btn"><HardDrive className="w-3.5 h-3.5 mr-1" />Connect Google Drive</Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="rfp-drive-connected">Drive connected</Badge>
                      {drive.folder?.id ? <span className="text-slate-600">Team folder: <b>{drive.folder.name || drive.folder.id}</b></span> : <span className="text-slate-500">No team folder nominated yet</span>}
                      <Button size="sm" variant="outline" className="h-6 text-[10px] ml-auto" onClick={() => listDrive(drive.folder?.id)} disabled={!drive.folder?.id} data-testid="rfp-drive-list-btn"><RefreshCw className="w-3 h-3 mr-1" />Browse folder</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={syncNow} disabled={syncing} title="Re-import Drive documents whose Drive modified time changed (also runs daily at 02:00 Sydney)" data-testid="rfp-drive-sync-btn">{syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" />Sync now</>}</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={disconnect} data-testid="rfp-drive-disconnect-btn"><Unplug className="w-3 h-3 mr-1" />Disconnect</Button>
                    </div>
                    {lastSync && <p className="text-[10px] text-slate-400" data-testid="rfp-drive-last-sync">Last sync {new Date(lastSync.at).toLocaleString()} · {lastSync.checked} checked · {lastSync.updated} updated{lastSync.errors?.length ? ` · ${lastSync.errors.length} error(s)` : ''} · daily at 02:00 Sydney</p>}
                    <div className="flex items-center gap-2">
                      <Link2 className="w-3.5 h-3.5 text-slate-400" />
                      <Input value={folderInput} onChange={e => setFolderInput(e.target.value)} placeholder="Paste a Drive folder link or ID to nominate it for the team" className="h-8 text-xs" data-testid="rfp-drive-folder-input" />
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={saveFolder} disabled={!folderInput.trim()} data-testid="rfp-drive-folder-save">Nominate</Button>
                    </div>
                    {driveFiles === 'loading' ? <p className="text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Listing…</p>
                      : Array.isArray(driveFiles) && (
                        <div className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="rfp-drive-files">
                          {driveFiles.length === 0 && <p className="text-xs text-slate-400 p-3">This folder is empty.</p>}
                          {driveFiles.map(f => (
                            <div key={f.id} className="p-2 flex items-center gap-2 text-xs" data-testid={`rfp-drive-file-${f.id}`}>
                              {f.is_folder ? <FolderOpen className="w-3.5 h-3.5 text-amber-500" /> : <FileText className="w-3.5 h-3.5 text-slate-400" />}
                              <span className="flex-1 truncate text-[#0F2040]">{f.name}</span>
                              <span className="text-[10px] text-slate-400">{f.is_folder ? '' : fmtSize(f.size)}</span>
                              {f.is_folder ? <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => listDrive(f.id)}>Open</Button>
                                : f.library_id ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">In library</Badge>
                                  : <Button size="sm" className="h-6 text-[10px] px-2 bg-[#4ABFCF] hover:bg-[#3AA9B9] text-white" disabled={importing[f.id]} onClick={() => importFile(f)} data-testid={`rfp-drive-import-${f.id}`}>{importing[f.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Import'}</Button>}
                            </div>
                          ))}
                        </div>
                      )}
                  </>
                )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
